import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ExternalLink } from '@lifeomic/chromicons';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles, useTheme } from '@lifeomic/chroma-react/styles';
import { useActiveProjectId, useServiceAuth } from '../../hooks';
import { getTestProps } from '../../util/testProps';
import {
  MemberExtractionResultReview,
  MemberExtractionReviewedHandler,
} from './MemberExtractionResultReview';
import { MemberExtractionValueHistory } from './MemberExtractionValueHistory';
import { heroChipStyle, heroSpace, heroSurface } from './memberExtractionTheme';
import {
  formatCollectionDate,
  requestMemberExtractionValueHistory,
  VALUE_HISTORY_PAGE_SIZE,
  valueHistoryFetchLimit,
} from './memberExtractionHistory';

export const HISTORY_PREFETCH_DELAY_MS = 150;
const HISTORY_TRANSITION_MS = 160;
// Rendered height of a chroma Button, so rows keep the same height once the
// approve/reject buttons give way to a review status.
const REVIEW_BUTTON_HEIGHT = 35;
import type { MemberExtractionResourceRow } from './useMemberExtractionDocuments';

const messages = defineMessages({
  loinc: {
    id: 'phc-web-toolkit.memberExtractions.loinc',
    defaultMessage: 'LOINC',
  },
});

const LOINC_SYSTEMS = new Set(['http://loinc.org', 'https://loinc.org']);
const LOINC_CODE = /^([A-Za-z]*)(\d+)-(\d)$/;

// LOINC check digits use the Luhn algorithm on the digits before the hyphen.
const loincCheckDigit = (digits: string) => {
  let sum = 0;
  let doubleDigit = true;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let value = Number(digits[index]);
    if (doubleDigit) {
      value *= 2;
      if (value > 9) {
        value -= 9;
      }
    }
    sum += value;
    doubleDigit = !doubleDigit;
  }
  return (10 - (sum % 10)) % 10;
};

export const loincPageUrl = (system: string, code: string) => {
  const normalized = system.trim().replace(/\/+$/, '').toLowerCase();
  const loincCode = code.trim();
  const match = LOINC_CODE.exec(loincCode);
  if (
    !LOINC_SYSTEMS.has(normalized) ||
    !match ||
    loincCheckDigit(match[2]) !== Number(match[3])
  ) {
    return undefined;
  }
  return `https://loinc.org/${encodeURIComponent(loincCode)}`;
};

const useResultRowStyles = makeStyles(() => ({
  '@keyframes historyRowIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  resourceRow: {
    cursor: 'pointer',
    borderRadius: heroSurface.radiusMedium,
    padding: heroSpace.tight,
    backgroundColor: 'transparent',
    transition: `background-color ${heroSurface.transition}`,
    '&:hover': {
      backgroundColor: heroSurface.muted,
    },
    '&:hover $caretFrame': {
      backgroundColor: '#fff',
      borderColor: '#afafaf',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  caretFrame: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    flex: '0 0 auto',
    boxSizing: 'border-box',
    width: 20,
    height: 20,
    margin: 0,
    padding: 0,
    border: '1px solid #b0b0b0',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    color: 'inherit',
    font: 'inherit',
    lineHeight: 0,
    cursor: 'pointer',
    transition: 'background-color 60ms ease-out, border-color 120ms ease-out',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  caret: {
    display: 'block',
    flex: '0 0 auto',
    width: 10,
    height: 10,
    transition: `transform ${HISTORY_TRANSITION_MS}ms linear`,
    transformOrigin: 'center',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  historyPanel: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transition: `grid-template-rows ${HISTORY_TRANSITION_MS}ms linear`,
    '&$historyPanelOpen': {
      gridTemplateRows: '1fr',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  historyPanelOpen: {},
  historyPanelInner: {
    overflow: 'hidden',
    minHeight: 0,
  },
  historyRow: {
    animation: `$historyRowIn ${HISTORY_TRANSITION_MS}ms linear`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
}));

const resultRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  alignItems: 'center',
  columnGap: heroSpace.regular,
  rowGap: heroSpace.tight,
};

const summaryRowStyle = {
  display: 'flex',
  gridColumn: 2,
  gridRow: 1,
  alignItems: 'center',
  minWidth: 0,
};

const titleLineStyle = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  gap: heroSpace.tight,
};

const historyPlacementStyle = {
  gridColumn: 2,
  gridRow: 2,
};

const resultDetailStyle = {
  display: 'flex',
  columnGap: heroSpace.regular,
};

const resultColumnStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  flex: '0 1 auto',
  minWidth: 0,
  rowGap: 0,
};

const plainButtonStyle = {
  padding: 0,
  border: 0,
  background: 'none',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'left' as const,
  cursor: 'pointer',
};

const useLoincPillStyles = makeStyles(theme => ({
  pill: {
    ...heroChipStyle,
    gap: 4,
    height: 20,
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: '0.02em',
    // Caps-only labels sit high in a normal line box; trimming it to the cap
    // height lets flex centering land on the letters themselves.
    textBox: 'trim-both cap alphabetic',
    '@supports not (text-box: trim-both cap alphabetic)': {
      paddingTop: 1,
    },
    textDecoration: 'none',
    cursor: 'pointer',
    backgroundColor: theme.palette.primary[50],
    color: theme.palette.primary.main,
    transition: `background-color ${heroSurface.transition}`,
    '&:hover': {
      backgroundColor: theme.palette.primary[100],
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
}));

const LoincPill: React.FC<{ href: string; label: string }> = ({
  href,
  label,
}) => {
  const classes = useLoincPillStyles();

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.pill}
      onClick={event => event.stopPropagation()}
      {...getTestProps('member-extraction-loinc-link')}
    >
      {label}
      <ExternalLink aria-hidden width={10} height={10} />
    </a>
  );
};

type MemberExtractionResultRowProps = {
  patientId: string;
  sourceReference: string;
  resource: MemberExtractionResourceRow;
  onReviewed?: MemberExtractionReviewedHandler;
};

export const MemberExtractionResultRow: React.FC<
  MemberExtractionResultRowProps
> = ({ patientId, sourceReference, resource, onReviewed }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const classes = useResultRowStyles();
  const [expanded, setExpanded] = React.useState(false);
  const [historyMounted, setHistoryMounted] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [visibleCount, setVisibleCount] = React.useState(
    VALUE_HISTORY_PAGE_SIZE,
  );
  const isLab = resource.resourceType === 'Observation';
  const historyCode = isLab ? resource.historyCode : undefined;
  const coding = resource.coding ?? historyCode;

  const toggle = () => {
    if (!historyCode) {
      return;
    }
    if (expanded) {
      setHistoryOpen(false);
    } else {
      setHistoryMounted(true);
    }
    setExpanded(!expanded);
  };

  React.useEffect(() => {
    if (expanded) {
      const frame = requestAnimationFrame(() => setHistoryOpen(true));
      return () => cancelAnimationFrame(frame);
    }
    if (!historyMounted) {
      return undefined;
    }
    // Stays mounted until the collapse transition has played out.
    const timer = setTimeout(() => {
      setHistoryMounted(false);
      setVisibleCount(VALUE_HISTORY_PAGE_SIZE);
    }, HISTORY_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [expanded, historyMounted]);

  const auth = useServiceAuth();
  const projectId = useActiveProjectId();
  const prefetchTimer = React.useRef<ReturnType<typeof setTimeout>>();

  const cancelPrefetch = () => {
    if (prefetchTimer.current) {
      clearTimeout(prefetchTimer.current);
      prefetchTimer.current = undefined;
    }
  };

  const prefetchHistory = () => {
    cancelPrefetch();
    if (!historyCode || expanded || !projectId || !auth?.authToken) {
      return;
    }
    requestMemberExtractionValueHistory(
      auth,
      projectId,
      patientId,
      historyCode.system,
      historyCode.code,
      valueHistoryFetchLimit(VALUE_HISTORY_PAGE_SIZE),
    ).catch(() => undefined);
  };

  const schedulePrefetch = () => {
    cancelPrefetch();
    if (!historyCode || expanded) {
      return;
    }
    prefetchTimer.current = setTimeout(
      prefetchHistory,
      HISTORY_PREFETCH_DELAY_MS,
    );
  };

  React.useEffect(() => cancelPrefetch, []);

  const loincHref = coding
    ? loincPageUrl(coding.system, coding.code)
    : undefined;
  const details =
    resource.collectedAt || resource.value ? (
      <>
        {resource.collectedAt ? (
          <Text size="caption">
            {formatCollectionDate(resource.collectedAt)}
          </Text>
        ) : null}
        {resource.value ? (
          <Text size="caption" style={{ color: theme.palette.primary.main }}>
            {resource.value}
          </Text>
        ) : null}
      </>
    ) : null;
  const caret = (
    <svg
      aria-hidden
      className={classes.caret}
      viewBox="0 0 12 12"
      style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
      {...getTestProps('member-extraction-result-caret')}
    >
      <path d="M4 2.5L10 6L4 9.5z" fill="currentColor" />
    </svg>
  );
  const title = (
    <Text size="subbody" style={{ display: 'inline', fontWeight: 500 }}>
      {resource.title}
    </Text>
  );
  const loincPill = loincHref ? (
    <LoincPill href={loincHref} label={formatMessage(messages.loinc)} />
  ) : null;

  const resultBody = (
    <div style={{ ...resultColumnStyle, flex: '1 1 auto' }}>
      <div style={titleLineStyle}>
        {title}
        {loincPill}
      </div>
      {details ? (
        historyCode ? (
          <button
            type="button"
            style={{ ...plainButtonStyle, ...resultDetailStyle }}
            onClick={toggle}
          >
            {details}
          </button>
        ) : (
          <div style={resultDetailStyle}>{details}</div>
        )
      ) : null}
    </div>
  );

  return (
    <li
      className={classes.resourceRow}
      style={{
        ...resultRowStyle,
        ...(isLab ? null : { gridTemplateColumns: 'minmax(0, 1fr)' }),
      }}
      onMouseEnter={schedulePrefetch}
      onMouseLeave={cancelPrefetch}
      onFocus={prefetchHistory}
      {...getTestProps('member-extraction-resource')}
    >
      {isLab ? (
        historyCode ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={resource.title}
            className={classes.caretFrame}
            onClick={toggle}
          >
            {caret}
          </button>
        ) : (
          <span
            aria-hidden
            className={classes.caretFrame}
            style={{ visibility: 'hidden' }}
          />
        )
      ) : null}
      <div
        style={{
          ...summaryRowStyle,
          gridColumn: isLab ? 2 : 1,
          minHeight: theme.pxToRem(REVIEW_BUTTON_HEIGHT),
        }}
      >
        {historyCode && !loincHref ? (
          <div
            style={{
              ...resultColumnStyle,
              flex: '1 1 auto',
              cursor: 'pointer',
            }}
            role="presentation"
            onClick={toggle}
          >
            <div>{title}</div>
            {details ? <div style={resultDetailStyle}>{details}</div> : null}
          </div>
        ) : (
          resultBody
        )}
        <MemberExtractionResultReview
          patientId={patientId}
          sourceReference={sourceReference}
          resource={resource}
          onReviewed={onReviewed}
        />
      </div>
      {historyMounted && historyCode ? (
        <div
          className={
            historyOpen
              ? `${classes.historyPanel} ${classes.historyPanelOpen}`
              : classes.historyPanel
          }
          style={historyPlacementStyle}
          role="presentation"
          onClick={toggle}
        >
          <div className={classes.historyPanelInner}>
            <MemberExtractionValueHistory
              patientId={patientId}
              system={historyCode.system}
              code={historyCode.code}
              excludeId={resource.id}
              historyRowClassName={classes.historyRow}
              visibleCount={visibleCount}
              onLoadMore={() =>
                setVisibleCount(count => count + VALUE_HISTORY_PAGE_SIZE)
              }
            />
          </div>
        </div>
      ) : null}
    </li>
  );
};
